import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, Lightbulb, Mail, Phone, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { DateInputFR } from '../../DateInputFR';
import { STATUTS_RECLAMATION, ORIGINES_RECLAMATION, CANAUX, Badge, aujourdhui } from '../constantes';
import { dateFr } from '../shared';

const select = 'h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm';
const Champ = ({ label, children, large }) => <label className={`block space-y-1 text-sm ${large ? 'sm:col-span-2' : ''}`}><span className="font-medium text-slate-700">{label}</span>{children}</label>;

// Saisie par le centre d'une réclamation reçue par e-mail, téléphone, courrier ou à l'oral
export const NouvelleReclamationDialog = ({ open, onClose, onSaved }) => {
  const vide = { origine: 'stagiaire', canal: 'telephone', auteur_nom: '', auteur_email: '', date_reception: aujourdhui(), objet: '', description: '' };
  const [f, setF] = useState(vide);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(vide); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const enregistrer = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post('/reclamations', f);
      toast.success(`Réclamation ${data.numero} enregistrée${data.accuse_envoye ? ' · accusé de réception envoyé' : ''}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="reclamation-nouvelle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Saisir une réclamation</DialogTitle>
          <DialogDescription>Reçue hors de l'application. Si vous indiquez un e-mail, l'accusé de réception part automatiquement.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Champ label="Origine"><select value={f.origine} onChange={set('origine')} className={select} data-testid="reclamation-origine">{Object.entries(ORIGINES_RECLAMATION).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
          <Champ label="Reçue par"><select value={f.canal} onChange={set('canal')} className={select} data-testid="reclamation-canal">{Object.entries(CANAUX).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
          <Champ label="Nom de la personne *"><Input value={f.auteur_nom} onChange={set('auteur_nom')} data-testid="reclamation-auteur-nom" /></Champ>
          <Champ label="E-mail (accusé de réception)"><Input type="email" value={f.auteur_email} onChange={set('auteur_email')} data-testid="reclamation-auteur-email" /></Champ>
          <Champ label="Reçue le"><DateInputFR value={f.date_reception} onChange={set('date_reception')} data-testid="reclamation-date" /></Champ>
          <Champ label="Objet *" large><Input value={f.objet} onChange={set('objet')} data-testid="reclamation-objet" /></Champ>
          <Champ label="Description *" large><Textarea rows={4} value={f.description} onChange={set('description')} data-testid="reclamation-description" /></Champ>
        </div>
        <Button onClick={enregistrer} disabled={busy || !f.auteur_nom.trim() || !f.objet.trim() || !f.description.trim()} className="w-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="reclamation-enregistrer">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer la réclamation'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const Suivi = ({ suivi = [] }) => (
  <ol className="space-y-1.5 border-l-2 border-slate-100 pl-3 text-xs" data-testid="reclamation-suivi">
    {suivi.map((s, i) => <li key={`${s.date}-${i}`}><span className="text-slate-400">{new Date(s.date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span> · <span className="text-slate-700">{s.action}</span>{s.par ? <span className="text-slate-400"> ({s.par})</span> : null}</li>)}
  </ol>
);

// Traitement d'une réclamation : statut, analyse des causes, réponse envoyée au déposant, création d'une action
export const ReclamationDialog = ({ reclamation: r, onClose, onSaved, onCreerAction }) => {
  const [f, setF] = useState({});
  const [envoyer, setEnvoyer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmer, setConfirmer] = useState(false);
  useEffect(() => { if (r) { setF({ statut: r.statut, analyse: r.analyse || '', reponse: r.reponse || '' }); setEnvoyer(false); setConfirmer(false); } }, [r]);
  if (!r) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const joignable = !!(r.auteur_email || r.auteur_id);
  const enregistrer = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`/reclamations/${r.reclamation_id}`, { ...f, envoyer_reponse: envoyer });
      toast.success(envoyer ? 'Réponse envoyée au déposant' : 'Réclamation mise à jour');
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  const supprimer = async () => {
    try { await axios.delete(`/reclamations/${r.reclamation_id}`); toast.success('Réclamation supprimée'); onSaved(null); } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="reclamation-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs font-semibold text-slate-500">{r.numero}</span><Badge meta={STATUTS_RECLAMATION[r.statut]} testId="reclamation-dialog-statut" /></div>
          <DialogTitle className="font-display text-xl">{r.objet}</DialogTitle>
          <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1">
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {r.auteur_nom} ({ORIGINES_RECLAMATION[r.origine] || r.origine})</span>
            {r.auteur_email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {r.auteur_email}</span>}
            <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {CANAUX[r.canal]} · reçue le {dateFr(r.date_reception)}</span>
          </DialogDescription>
        </DialogHeader>
        <p className="whitespace-pre-line rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid="reclamation-dialog-description">{r.description}</p>
        <div className="grid gap-3">
          <Champ label="Statut"><select value={f.statut} onChange={set('statut')} className={select} data-testid="reclamation-statut">{Object.entries(STATUTS_RECLAMATION).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Champ>
          <Champ label="Analyse des causes (interne)"><Textarea rows={2} value={f.analyse} onChange={set('analyse')} data-testid="reclamation-analyse" /></Champ>
          <Champ label="Réponse au déposant"><Textarea rows={3} value={f.reponse} onChange={set('reponse')} data-testid="reclamation-reponse" /></Champ>
          {joignable && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={envoyer} onChange={(e) => setEnvoyer(e.target.checked)} disabled={!f.reponse?.trim()} className="h-4 w-4 accent-[#8DC63F]" data-testid="reclamation-envoyer" />
              <Send className="h-4 w-4 text-slate-400" /> {r.reponse_envoyee_le ? `Renvoyer la réponse (déjà envoyée le ${dateFr(r.reponse_envoyee_le)})` : 'Envoyer la réponse au déposant (e-mail + espace FISL)'}
            </label>
          )}
        </div>
        {(r.actions || []).length > 0 && <p className="text-sm text-slate-600">Actions liées : {r.actions.map((a) => a.numero).join(', ')}</p>}
        <Suivi suivi={r.suivi} />
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {confirmer
            ? <span className="flex gap-1"><Button variant="destructive" size="sm" onClick={supprimer} data-testid="reclamation-supprimer-confirm">Confirmer la suppression</Button><Button variant="ghost" size="sm" onClick={() => setConfirmer(false)}>Annuler</Button></span>
            : <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setConfirmer(true)} data-testid="reclamation-supprimer">Supprimer</Button>}
          <span className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => onCreerAction(r)} data-testid="reclamation-creer-action"><Lightbulb className="h-4 w-4 mr-1" /> Créer une action d'amélioration</Button>
            <Button onClick={enregistrer} disabled={busy} className="bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="reclamation-sauver">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}</Button>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
